/**
 * SurveyDashboard — HR analytics dashboard for Employee Survey cycles.
 *
 * Implements the requested "Google Forms"-style analytics view:
 *   - Overview stats + completion trend
 *   - Per-question breakdown (rating distribution, multiple-choice / checkbox
 *     option shares, yes/no split, searchable text answers)
 *   - Insights panel (avg completion time, most/least answered question,
 *     most selected option, response rate)
 *   - Anonymized response browser ("Response #N" — never an employee name/ID)
 *   - CSV export
 *
 * Fully anonymous: no component here ever receives or renders employee
 * identity — the backend API deliberately never serializes it.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData } from "../../../store/actions";
import { stripHtml } from "../../../utils/richTextUtils";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import {
  surveyCyclesUrl,
  surveyCycleDashboardUrl,
  surveyCycleResponsesUrl,
  surveyCycleExportUrl,
} from "../../../Service/Apis";

const COLORS = ["#2e7d32", "#1976d2", "#ed6c02", "#9c27b0", "#d32f2f", "#0288d1", "#795548"];
// Coloured left-border accent per question in the response detail dialog —
// mirrors the per-topic accent colours used elsewhere (e.g. Peer Review).
const QUESTION_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c62828", "#00838f"];

// ── Types ────────────────────────────────────────────────────────────────────

interface CycleOption {
  id: string;
  name: string;
  status: string;
}

interface QuestionStat {
  question_id: string;
  question_text: string;
  question_type: "rating" | "multiple_choice" | "checkbox" | "yes_no" | "text";
  is_mandatory: boolean;
  rating_scale?: number;
  average?: number | null;
  distribution?: Record<string, number>;
  distribution_percentage?: Record<string, number>;
  options?: Array<{ label: string; count: number; percentage: number }>;
  yes_count?: number;
  no_count?: number;
  yes_percentage?: number;
  no_percentage?: number;
  text_answers?: string[];
  response_count: number;
}

interface DashboardData {
  cycle_id: string;
  cycle_name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  response_count: number;
  employee_count: number;
  completion_percentage: number;
  completion_trend: Array<{ date: string; count: number }>;
  avg_completion_time_seconds: number;
  questions: QuestionStat[];
  most_answered_question: { question_text: string; response_count: number; percentage: number } | null;
  least_answered_question: { question_text: string; response_count: number; percentage: number } | null;
  most_selected_option: { label: string; count: number; percentage: number } | null;
}

interface AnonymousResponse {
  response_number: number;
  submitted_at: string | null;
  answers: Array<{
    question_id: string;
    question_text: string;
    question_type: string;
    rating_value: number | null;
    rating_scale: number | null;
    rating_style: string | null;
    text_value: string;
    yes_no_value: boolean | null;
    selected_options: string[];
  }>;
}

/** Formats a duration in seconds as "M min S sec" (used for avg. completion time). */
const formatDuration = (seconds: number): string => {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m} min ${s} sec`;
};

// (rating_style, rating_scale) -> {point: anchor label}. Mirrors the map in
// SurveyForm.tsx / SurveyQuestion.RATING_ANCHOR_LABELS on the backend, so an
// individual rating answer can be labelled the same way the employee saw it
// (e.g. "3 / 5 - Neutral").
const RATING_ANCHOR_LABELS: Record<string, Record<number, string>> = {
  agreement_5: { 1: "Strongly Disagree", 2: "Disagree", 3: "Neutral", 4: "Agree", 5: "Strongly Agree" },
  agreement_3: { 1: "Disagree", 2: "Neutral", 3: "Agree" },
  happiness_3: { 1: "Not Happy", 2: "Neutral", 3: "Happy" },
  frequency_3: { 1: "No", 2: "Sometimes", 3: "Yes" },
};

/** Formats a single rating answer as "value / scale" plus its anchor label, e.g. "3 / 5 - Neutral". */
const formatRatingAnswer = (value: number | null, scale: number | null, style: string | null): string => {
  if (value == null) return "—";
  const effectiveScale = scale ?? 5;
  const label = RATING_ANCHOR_LABELS[`${style ?? "agreement"}_${effectiveScale}`]?.[value];
  return label ? `${value} / ${effectiveScale} - ${label}` : `${value} / ${effectiveScale}`;
};

// ── Main component ───────────────────────────────────────────────────────────

/**
 * SurveyDashboard — HR analytics dashboard (see file header for the full
 * feature list). Everything here is scoped to a single, HR-selected survey
 * cycle (`selectedCycleId`, synced to the `?cycle=` URL param) and refetches
 * both the aggregate dashboard payload and the paginated anonymized
 * response list whenever that cycle changes.
 */
const SurveyDashboard: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [cycles, setCycles] = useState<CycleOption[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>(searchParams.get("cycle") || "");
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);

  const [responseSearch, setResponseSearch] = useState("");
  const [debouncedResponseSearch, setDebouncedResponseSearch] = useState("");
  const [responses, setResponses] = useState<AnonymousResponse[]>([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [viewingResponse, setViewingResponse] = useState<AnonymousResponse | null>(null);
  const [responsesTotal, setResponsesTotal] = useState(0);
  const [responsePage, setResponsePage] = useState(0);
  const [responseRowsPerPage, setResponseRowsPerPage] = useState(10);

  // Loads the cycle picker options once on mount, defaulting to the first
  // cycle (or the one preselected via the `?cycle=` URL param) if none is
  // selected yet.
  useEffect(() => {
    dispatch(fetchData(`${surveyCyclesUrl}?page_size=100`)).then((res: any) => {
      const list = (res?.results ?? res ?? []).map((c: any) => ({ id: c.id, name: c.name, status: c.status }));
      setCycles(list);
      if (!selectedCycleId && list.length > 0) {
        setSelectedCycleId(list[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loads the aggregate dashboard payload for the selected cycle, and keeps
  // the `?cycle=` URL param in sync so the dashboard is linkable/bookmarkable.
  useEffect(() => {
    if (!selectedCycleId) return;
    setSearchParams({ cycle: selectedCycleId });
    setLoading(true);
    dispatch(fetchData(surveyCycleDashboardUrl(selectedCycleId)))
      .then((res: any) => setDashboard(res))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycleId]);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedResponseSearch(responseSearch), 400);
    return () => clearTimeout(t);
  }, [responseSearch]);

  // Reset to page 1 whenever the cycle or search term changes.
  useEffect(() => {
    setResponsePage(0);
  }, [selectedCycleId, debouncedResponseSearch]);

  // Loads one page of anonymized individual responses (search + pagination),
  // refetching whenever the cycle, page, page size, or debounced search term changes.
  useEffect(() => {
    if (!selectedCycleId) return;
    setResponsesLoading(true);
    const params = new URLSearchParams();
    if (debouncedResponseSearch) params.set("search", debouncedResponseSearch);
    params.set("page", String(responsePage + 1));
    params.set("page_size", String(responseRowsPerPage));
    dispatch(fetchData(`${surveyCycleResponsesUrl(selectedCycleId)}?${params.toString()}`))
      .then((res: any) => {
        setResponses(res?.results ?? res ?? []);
        setResponsesTotal(res?.count ?? (res?.results ?? res ?? []).length);
      })
      .finally(() => setResponsesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycleId, responsePage, responseRowsPerPage, debouncedResponseSearch]);

  /** Reshapes the backend's completion-trend series into the {date, Responses} shape Recharts expects. */
  const trendChartData = useMemo(
    () => (dashboard?.completion_trend ?? []).map((d) => ({ date: d.date, Responses: d.count })),
    [dashboard]
  );

  /** Downloads the anonymized responses for the selected cycle as a CSV file. */
  const handleExport = () => {
    if (!selectedCycleId) return;
    const token = localStorage.getItem("token");
    fetch(surveyCycleExportUrl(selectedCycleId), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `survey_${selectedCycleId}_responses.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <PageTitle>Employee Survey Dashboard</PageTitle>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Survey Cycle</InputLabel>
            <Select
              value={selectedCycleId}
              label="Survey Cycle"
              onChange={(e) => setSelectedCycleId(e.target.value)}
            >
              {cycles.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} {c.status === "active" ? "(Active)" : c.status === "closed" ? "(Closed)" : "(Draft)"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!selectedCycleId}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !dashboard ? (
        <Alert severity="info">Select a survey cycle to view analytics.</Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            All data shown here is fully anonymous — no employee names, IDs, or emails are ever displayed.
          </Alert>

          {/* 1. Survey Overview */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Card elevation={2} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>{dashboard.cycle_name}</Typography>
                  <Stack direction="row" spacing={1} mb={1}>
                    <Chip
                      label={dashboard.status.charAt(0).toUpperCase() + dashboard.status.slice(1)}
                      color={dashboard.status === "active" ? "success" : dashboard.status === "closed" ? "error" : "default"}
                      size="small"
                    />
                    {dashboard.start_date && (
                      <Chip label={`Started: ${dashboard.start_date}`} size="small" variant="outlined" />
                    )}
                    {dashboard.end_date && (
                      <Chip label={`Ends: ${dashboard.end_date}`} size="small" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Responses: {dashboard.response_count} / {dashboard.employee_count} ({dashboard.completion_percentage}%)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, dashboard.completion_percentage)}
                    sx={{ height: 10, borderRadius: 5, mb: 2 }}
                  />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Completion Trend</Typography>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={trendChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="Responses" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* 7. Survey Insights */}
            <Grid item xs={12} md={5}>
              <Card elevation={2} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>Survey Insights</Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Average Completion Time</Typography>
                      <Typography variant="body1" fontWeight={600}>{formatDuration(dashboard.avg_completion_time_seconds)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Most Answered Question</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {dashboard.most_answered_question
                          ? `"${stripHtml(dashboard.most_answered_question.question_text)}" (${dashboard.most_answered_question.percentage}%)`
                          : "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Least Answered / Most Skipped Question</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {dashboard.least_answered_question
                          ? `"${stripHtml(dashboard.least_answered_question.question_text)}" (${dashboard.least_answered_question.percentage}%)`
                          : "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Most Selected Option</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {dashboard.most_selected_option
                          ? `"${dashboard.most_selected_option.label}" (${dashboard.most_selected_option.percentage}%)`
                          : "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Response Rate</Typography>
                      <Typography variant="body1" fontWeight={600}>{dashboard.completion_percentage}%</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 2-6. Question-wise Analytics */}
          <Typography variant="h6" fontWeight={700} mb={1.5}>Question-wise Analytics</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {dashboard.questions.map((q, idx) => (
              <Grid item xs={12} md={6} key={q.question_id}>
                <Card elevation={2} sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      Q{idx + 1}. {stripHtml(q.question_text)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                      {q.response_count} response{q.response_count !== 1 ? "s" : ""}
                    </Typography>

                    {q.question_type === "rating" && (
                      <>
                        <Typography variant="body2" fontWeight={600} mb={1}>
                          Average Rating: {q.average != null ? q.average.toFixed(2) : "—"} / {q.rating_scale}
                        </Typography>
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart
                            data={Object.entries(q.distribution ?? {}).map(([k, v]) => ({
                              value: k,
                              count: v,
                              pct: q.distribution_percentage?.[k] ?? 0,
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="value" tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <RechartsTooltip formatter={(value: any, name: any, props: any) => [`${value} (${props.payload.pct}%)`, "Count"]} />
                            <Bar dataKey="count" fill="#1976d2" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </>
                    )}

                    {(q.question_type === "multiple_choice" || q.question_type === "checkbox") && (
                      <ResponsiveContainer width="100%" height={200}>
                        {q.question_type === "multiple_choice" ? (
                          <PieChart>
                            <Pie
                              data={q.options ?? []}
                              dataKey="count"
                              nameKey="label"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={(entry) => `${entry.label} ${entry.percentage}%`}
                            >
                              {(q.options ?? []).map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        ) : (
                          <BarChart data={q.options ?? []} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }} />
                            <RechartsTooltip />
                            <Bar dataKey="count" fill="#9c27b0" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    )}

                    {q.question_type === "yes_no" && (
                      <Box>
                        <Stack direction="row" spacing={2} mb={1}>
                          <Chip label={`Yes: ${q.yes_percentage ?? 0}%`} color="success" size="small" />
                          <Chip label={`No: ${q.no_percentage ?? 0}%`} color="error" size="small" />
                        </Stack>
                        <ResponsiveContainer width="100%" height={120}>
                          <BarChart
                            data={[
                              { name: "Yes", count: q.yes_count ?? 0 },
                              { name: "No", count: q.no_count ?? 0 },
                            ]}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <RechartsTooltip />
                            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                              <Cell fill="#2e7d32" />
                              <Cell fill="#d32f2f" />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    )}

                    {q.question_type === "text" && (
                      <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                        {(q.text_answers ?? []).length === 0 ? (
                          <Typography variant="body2" color="text.disabled" fontStyle="italic">
                            No text responses yet.
                          </Typography>
                        ) : (
                          (q.text_answers ?? []).map((t, i) => (
                            <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.75 }}>
                              <Typography variant="body2">• {t}</Typography>
                            </Paper>
                          ))
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Anonymized response browser */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
            <Typography variant="h6" fontWeight={700}>Individual Responses (Anonymous)</Typography>
            <TextField
              size="small"
              placeholder="Search text answers…"
              value={responseSearch}
              onChange={(e) => setResponseSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.disabled" }} /> }}
              sx={{ minWidth: 240 }}
            />
          </Box>

          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    background: "#1E7B44",
                    "& .MuiTableCell-head": { color: "white", fontWeight: 700, fontSize: "0.82rem", py: 1.5 },
                  }}
                >
                  <TableCell>Response</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="center">Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {responsesLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : responses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No responses match your search.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  responses.map((r, idx) => (
                    <TableRow
                      key={r.response_number}
                      hover
                      sx={{
                        cursor: "pointer",
                        bgcolor: idx % 2 === 0 ? "white" : "grey.50",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                      onClick={() => setViewingResponse(r)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>Response #{r.response_number}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View response">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingResponse(r);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <StandardTablePagination
            count={responsesTotal}
            page={responsePage}
            rowsPerPage={responseRowsPerPage}
            onPageChange={(_e, newPage) => setResponsePage(newPage)}
            onRowsPerPageChange={(e) => {
              setResponseRowsPerPage(parseInt(e.target.value, 10));
              setResponsePage(0);
            }}
          />
        </>
      )}

      {/* Response detail dialog — anonymous only */}
      <Dialog open={!!viewingResponse} onClose={() => setViewingResponse(null)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" fontWeight={700}>
            Response #{viewingResponse?.response_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {viewingResponse?.submitted_at ? new Date(viewingResponse.submitted_at).toLocaleString() : "—"}
            &nbsp;·&nbsp; Fully anonymous — no employee identity attached
          </Typography>
          <IconButton
            onClick={() => setViewingResponse(null)}
            size="small"
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600, width: "60%" }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Answer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewingResponse?.answers.map((a, qi) => {
                  const color = QUESTION_COLORS[qi % QUESTION_COLORS.length];
                  return (
                    <TableRow key={a.question_id} hover>
                      <TableCell>
                        <Box display="flex" gap={1} alignItems="flex-start">
                          <Box
                            sx={{
                              mt: "2px",
                              minWidth: 22,
                              height: 22,
                              borderRadius: "50%",
                              bgcolor: color,
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {qi + 1}
                          </Box>
                          <Typography variant="body2">{stripHtml(a.question_text)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {a.question_type === "rating" && (
                          <Chip
                            label={formatRatingAnswer(a.rating_value, a.rating_scale, a.rating_style)}
                            size="small"
                            color={a.rating_value != null ? "primary" : "default"}
                            variant="outlined"
                          />
                        )}
                        {a.question_type === "text" && (
                          <Typography variant="body2" color={a.text_value ? "text.primary" : "text.disabled"}>
                            {a.text_value || "—"}
                          </Typography>
                        )}
                        {a.question_type === "yes_no" && (
                          <Chip
                            label={a.yes_no_value === null ? "—" : a.yes_no_value ? "Yes" : "No"}
                            size="small"
                            color={a.yes_no_value === null ? "default" : a.yes_no_value ? "success" : "error"}
                          />
                        )}
                        {(a.question_type === "multiple_choice" || a.question_type === "checkbox") && (
                          a.selected_options.length > 0 ? (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {a.selected_options.map((opt, oi) => (
                                <Chip key={oi} label={opt} size="small" variant="outlined" />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default SurveyDashboard;
